import { forwardRef, useMemo } from 'react';
import styled from '@emotion/styled';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import type { RootNode } from '@trinity/chat/src/types/rich-text';
import { Lightbulb } from '@trinity/chat-components/components/Chat.Widget/Lightbulb.js';
import { BasicRichText } from '../BasicRichText/BasicRichText';

type Props = {
  title: string;
  backgroundColor: string;
  values: string[];
  minValues: string[];
  maxValues: string[];
  labels: string[];
  colors?: string[];
  units?: string[];
  insights?: string;
};

function getGridLayout(count: number): string {
  switch (count) {
    case 1: return '1fr';
    case 2:
    case 3: return `repeat(${count}, 1fr)`;
    case 4: return 'repeat(2, 1fr)';
    case 5:
    case 6: return 'repeat(3, 1fr)';
    default: return '1fr';
  }
}

function getGridRows(count: number): string {
  switch (count) {
    case 1:
    case 2:
    case 3: return '1fr';
    case 4:
    case 6: return 'repeat(2, 1fr)';
    case 5: return 'auto auto';
    default: return '1fr';
  }
}

function computeNormalizedValue(rawValue: number, rawMin: number, rawMax: number): number {
  if (!Number.isFinite(rawValue) || !Number.isFinite(rawMin) || !Number.isFinite(rawMax)) return 0;
  if (rawMax === rawMin) return rawValue === rawMin ? 100 : 0;
  const ratio = (rawValue - rawMin) / (rawMax - rawMin);
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(100, Math.max(0, ratio * 100));
}

export const SemiCircleProgressWidget = forwardRef<HTMLDivElement, Props>(({
  title,
  backgroundColor,
  values,
  minValues,
  maxValues,
  labels,
  colors,
  units,
  insights,
}, ref) => {
  const validValues = values.slice(0, 6);
  const validLabels = labels.slice(0, 6);
  const validMinValues = minValues.slice(0, 6);
  const validMaxValues = maxValues.slice(0, 6);
  const validColors = colors ? colors.slice(0, 6) : undefined;
  const validUnits = units ? units.slice(0, 6) : Array(6).fill('%');

  // Memoized insights parsing
  const renderedInsights = useMemo(() => {
    if (!insights) return null;
    try {
      const parsedInsights = JSON.parse(insights) as RootNode;
      return <BasicRichText value={parsedInsights} />;
    } catch {
      return null;
    }
  }, [insights]);

  return (
    <Container ref={ref} backgroundColor={backgroundColor}>
      <Header>
        <Title>{title}</Title>
      </Header>

      <ValuesGrid
        count={validValues.length}
        gridColumns={getGridLayout(validValues.length)}
        gridRows={getGridRows(validValues.length)}>
        {validValues.map((value, index) => {
          const label = validLabels[index] || `Semi-Circle ${index + 1}`;
          const rawNumericValue = Number(value);
          const rawMinValue = Number(validMinValues[index] ?? 0);
          const rawMaxValue = Number(validMaxValues[index] ?? 100);

          const numericValue = Number.isFinite(rawNumericValue) ? rawNumericValue : 0;
          const minValue = Number.isFinite(rawMinValue) ? rawMinValue : 0;
          const maxValue = Number.isFinite(rawMaxValue) ? rawMaxValue : 100;

          const normalizedValue = computeNormalizedValue(rawNumericValue, rawMinValue, rawMaxValue);
          const displayValue = `${numericValue}${validUnits[index] ?? '%'}`;
          const arcColor = validColors?.[index] ?? '#4A90E2';

          // For 5-item layout: last 2 items are centered in the second row
          const gridColumn =
            validValues.length === 5 && index >= 3
              ? `${index - 2} / ${index - 1}`
              : undefined;

          return (
            <GaugeItem key={index} gridColumn={gridColumn}>
              <GaugeWrapper>
                {/* Min label */}
                <MinMaxLabel side="left">{minValue}</MinMaxLabel>

                <Gauge
                  value={normalizedValue}
                  startAngle={-90}
                  endAngle={90}
                  width={140}
                  height={80}
                  text={displayValue}
                  sx={{
                    [`& .${gaugeClasses.valueArc}`]: {
                      fill: arcColor,
                    },
                    [`& .${gaugeClasses.referenceArc}`]: {
                      fill: '#e0e0e0',
                    },
                    [`& .${gaugeClasses.valueText}`]: {
                      fontSize: '14px',
                      fontWeight: 700,
                      fill: 'currentColor',
                      transform: 'translate(0px, -4px)',
                    },
                  }}
                />

                {/* Max label */}
                <MinMaxLabel side="right">{maxValue}</MinMaxLabel>
              </GaugeWrapper>

              <GaugeLabel>{label}</GaugeLabel>
            </GaugeItem>
          );
        })}
      </ValuesGrid>

      {insights && (
        <Insights>
          <Lightbulb fill={'#4c4c4c'} size={12} />
          {renderedInsights}
        </Insights>
      )}
    </Container>
  );
});

SemiCircleProgressWidget.displayName = 'SemiCircleProgressWidget';

const Container = styled.div<{ backgroundColor: string }>(({ backgroundColor, theme }) => ({
  backgroundColor: backgroundColor || '#ffffff',
  containerType: 'inline-size',
  color: theme.palette.text.primary,
  borderRadius: '1.125em',
  padding: '1.25em',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '1em',
  boxSizing: 'border-box',
}));

const Header = styled.div({
  textAlign: 'left',
});

const Title = styled.h3(({ theme }) => ({
  margin: 0,
  fontSize: '1em',
  fontWeight: 700,
  color: theme.palette.text.primary,
  '@container (max-width: 200px)': {
    fontSize: '0.80em',
  },
}));

const ValuesGrid = styled.div<{
  count: number;
  gridColumns: string;
  gridRows: string;
}>(({ count, gridColumns, gridRows }) => ({
  display: 'grid',
  gridTemplateColumns: gridColumns,
  gridTemplateRows: gridRows,
  gap: '1em',
  flex: 1,
  alignItems: 'center',
  justifyItems: 'center',
  overflowX: 'auto',
  ...(count === 1 && {
    justifyContent: 'center',
    alignContent: 'center',
  }),
  '@container (max-width: 200px)': {
    gap: '0.5em',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto',
  },
  '@container (max-width: 140px)': {
    gap: '0.5em',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto',
  },
}));

const GaugeItem = styled.div<{ gridColumn?: string }>(({ gridColumn }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.25em',
  width: '100%',
  ...(gridColumn && { gridColumn }),
}));

const GaugeWrapper = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  width: '100%',
});

const MinMaxLabel = styled.span<{ side: 'left' | 'right' }>(({ side }) => ({
  position: 'absolute',
  bottom: 0,
  ...(side === 'left' ? { left: '50%', transform: 'translateX(-70px)' } : { left: '50%', transform: 'translateX(50px)' }),
  fontSize: '0.7em',
  color: '#888',
  lineHeight: 1,
  userSelect: 'none',
}));

const GaugeLabel = styled.div(({ theme }) => ({
  fontSize: '0.8em',
  fontWeight: 500,
  color: theme.palette.text.primary,
  textAlign: 'center',
  lineHeight: 1.3,
  '@container (max-width: 200px)': {
    fontSize: '0.7em',
  },
}));

const Insights = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.375em',
  fontSize: '0.8em',
  color: '#4c4c4c',
});
